import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { QueryClient, QueryClientProvider } from 'react-query';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import { TextButton } from '@components/Button/variants';
import IconClose from '@components/Images/Rebranding/IconClose';
import {
  useSubscribeToNewsletter,
  useUnsubscribeFromNewsletter,
  useUserOnboardingIncrementAttempt,
} from '@hooks';
import { useEventTracking } from '@hooks';
import useProfileUpdate from '@hooks/useProfileUpdate';
import useUserOnboardingUpdate from '@hooks/useUserOnboardingUpdate';
import Modal from '@mui/material/Modal';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';
import { User } from '@utils/backend-api/currentUser';
import { Newsletter } from '@utils/backend-api/newsletters';
import {
  SubscribeSource,
  UserOnboarding,
  hasShownOnboardingThisSession,
  markOnboardingShownThisSession,
} from '@utils/backend-api/userOnboarding';
import { updateProfile } from '@utils/backend-api/userProfile';

import ProgressBar from './ProgressBar';
import StepContent from './StepContent';
import StepHero from './StepHero';
import { OnboardingSteps, getNextStep, getStepsForSource } from './interface';

const queryClient = new QueryClient();

export interface Topic {
  key: string;
  label: string;
  default: boolean;
}

interface UserOnboardingModalProps {
  userOnboarding: UserOnboarding;
  initialCurrentUser: User;
  isProSubscriber: boolean;
  newsletters: Newsletter[];
  topics: Topic[];
}

const UserOnboardingContent = (props: UserOnboardingModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<OnboardingSteps | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasIncrementedAttempt, setHasIncrementedAttempt] = useState(false);
  const stepSaveRef = useRef<(() => void | Promise<void>) | null>(null);
  const { initialCurrentUser, isProSubscriber, newsletters, topics, userOnboarding } = props;
  const Button = isProSubscriber ? ProButton : PrimaryButton;
  const subscribeSource = userOnboarding.subscribeSource;
  const { trackEvent } = useEventTracking();

  const { updateUserOnboardingAsync } = useUserOnboardingUpdate();
  const { incrementOnboardingAttemptAsync } = useUserOnboardingIncrementAttempt();
  const { saveProfile } = useProfileUpdate({ userId: initialCurrentUser.id });
  const { mutateAsync: subscribeAsync } = useSubscribeToNewsletter();
  const { mutateAsync: unsubscribeAsync } = useUnsubscribeFromNewsletter();

  useEffect(() => {
    if (hasIncrementedAttempt) {
      return;
    }

    const run = async () => {
      const shouldShowImmediately = userOnboarding.shouldShowImmediately ?? false;
      const shownThisSession = hasShownOnboardingThisSession();

      const shouldShow = shouldShowImmediately || !shownThisSession;

      if (!shouldShow) {
        setIsOpen(false);
        return;
      }

      const lastCompleted = userOnboarding.lastStepCompleted || 0;
      const steps = getStepsForSource(userOnboarding.subscribeSource);

      // Find the next step based on lastCompleted
      let nextStep: OnboardingSteps | null = null;
      for (const s of steps) {
        if (s > lastCompleted) {
          nextStep = s;
          break;
        }
      }

      // If no next step found, don't show
      if (!nextStep) {
        setIsOpen(false);
        return;
      }

      try {
        await incrementOnboardingAttemptAsync();
      } catch (err) {
        console.error('Failed to increment onboarding attempt:', err);
      }

      markOnboardingShownThisSession();
      setHasIncrementedAttempt(true);
      setStep(nextStep);
      setIsOpen(true);
    };

    run();
  }, [userOnboarding, hasIncrementedAttempt, incrementOnboardingAttemptAsync]);

  const handleClose = () => {
    setIsOpen(false);
    trackEvent(click({ clickTarget: ClickTarget.Close }));
  };

  const onStepComplete = () => {
    const next = getNextStep(step, subscribeSource);
    if (next) setStep(next);
  };

  const handleNext = async () => {
    if (!step) return;

    const saveFn = stepSaveRef.current;
    setIsSaving(true);
    trackEvent(click({ clickTarget: `step_${step}_continue`, clickText: 'Continue' }));

    try {
      // Execute the save function if present
      if (saveFn && typeof saveFn === 'function') {
        await Promise.resolve(saveFn());
      }

      // Update onboarding with the completed step
      await updateUserOnboardingAsync({ lastStepCompleted: step });
      onStepComplete();
    } catch (err) {
      console.error('UserOnboarding save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = async () => {
    if (!step) return;
    trackEvent(click({ clickTarget: `step_${step}_skip`, clickText: 'Skip' }));
    onStepComplete();
  };

  return (
    <Modal
      className="UserOnBoardingModal flex items-center justify-center"
      open={isOpen}
      disableAutoFocus
      disableEnforceFocus
    >
      <div>
        <InView
          triggerOnce={true}
          onChange={(inView) => {
            inView &&
              trackEvent(impression({ impressionElement: ImpressionElement.UserOnboardingModal }));
          }}
        >
          <div
            id="user-onboarding-modal"
            className="flex h-screen w-screen bg-white sm:mx-16 sm:h-[640px] sm:w-[400px] md:h-[650px] md:w-[1256px]"
          >
            <div className="hidden flex-1 md:block">
              <StepHero
                step={step}
                isProSubscriber={isProSubscriber}
                subscribeSource={subscribeSource}
              />
            </div>
            <div className="relative flex min-h-0 flex-1 flex-col bg-white md:bg-grayscale-beige">
              {step !== OnboardingSteps.FINAL ? (
                <div className="block md:hidden">
                  <ProgressBar
                    step={step}
                    isProSubscriber={isProSubscriber}
                    subscribeSource={subscribeSource}
                  />
                </div>
              ) : (
                <IconClose
                  className={clsx(
                    `close-button absolute right-24 top-16 w-32 cursor-pointer text-black`
                  )}
                  onClick={handleClose}
                  role="button"
                  aria-pressed="false"
                />
              )}
              <div className="min-h-0 flex-1 overflow-y-auto">
                <StepContent
                  step={step}
                  user={initialCurrentUser}
                  topics={topics}
                  newsletters={newsletters}
                  stepSaveRef={stepSaveRef}
                  onStepComplete={onStepComplete}
                  saveProfile={saveProfile}
                  updateProfile={updateProfile}
                  subscribeAsync={subscribeAsync}
                  unsubscribeAsync={unsubscribeAsync}
                  subscribeSource={subscribeSource}
                />
              </div>
              {step !== OnboardingSteps.FINAL && (
                <div
                  className={clsx(
                    'flex shrink-0 flex-col items-center gap-8 border-0 border-t border-solid border-grayscale-beige px-16 py-16 md:border-grayscale-charcoal md:px-24'
                  )}
                >
                  <Button
                    as="button"
                    onClick={handleNext}
                    disabled={isSaving}
                    className={clsx('!w-[340px]', {
                      'hover:!border-pro-gold': isProSubscriber && !isSaving,
                    })}
                  >
                    {isSaving ? 'Saving…' : 'Continue'}
                  </Button>
                  <TextButton
                    as="button"
                    onClick={handleSkip}
                    className="font-suisse-intl text-button-sm"
                  >
                    Skip
                  </TextButton>
                </div>
              )}
            </div>
          </div>
        </InView>
      </div>
    </Modal>
  );
};

const UserOnboardingModal = (props: UserOnboardingModalProps) => {
  const {
    userOnboarding: { subscribeSource },
  } = props;
  const location =
    subscribeSource === SubscribeSource.PROMO
      ? TrackingLocation.UserOnboardingRegular
      : TrackingLocation.UserOnboardingInterrupted;
  const { Track } = useEventTracking({ location });
  return (
    <Track>
      <QueryClientProvider client={queryClient}>
        <UserOnboardingContent {...props} />
      </QueryClientProvider>
    </Track>
  );
};

export default UserOnboardingModal;
